import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PortalIdParser from 'PortalIdParser';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H2 from 'UIComponents/elements/headings/H2';
import H3 from 'UIComponents/elements/headings/H3';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UIFlex from 'UIComponents/layout/UIFlex';

// @ts-expect-error untyped dependency
import LoadingWrapper from 'ecosystem-marketplace-core/components/LoadingWrapper';
import ReviewsListStats from 'marketplace-ui-reviews/components/ReviewsListStats';
import ReviewsHeader from 'marketplace-ui-reviews/components/ReviewsHeader';
import ReviewsFilterSortDropdowns from 'marketplace-ui-reviews/components/ReviewsFilterSortDropdowns';
import ReviewsPaginatedList from 'marketplace-ui-reviews/components/ReviewsPaginatedList';
import { RELEVANCE, REVIEWS_PER_PAGE, SEARCH_KEY } from 'marketplace-ui-reviews/constants/ReviewsListQueries';
import { useReviewability } from 'marketplace-ui-reviews/hooks/useReviewability';
import * as ReviewTypes from 'marketplace-ui-reviews/constants/ReviewType';
import ReviewFilters from 'marketplace-ui-reviews/records/ReviewFilters';
import { useReviews } from 'marketplace-ui-reviews/hooks/useReviews';
import { usePrevious } from 'marketplace-ui-common/hooks/usePrevious';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
import DetailsReviewRenderer from './DetailsReviewRenderer';
import WriteReviewButton from './WriteReviewButton';
// @ts-expect-error untyped dependency
import { getIsPublic } from '../../../selectors/auth';
import ReviewsListEmptyState from './ReviewsListEmptyState';
import { AppReviewWizard } from './AppReviewWizard';
import { replace } from '../../../utils/history';
import { REVIEWS_LIST_WIDTH, MINIMUM_REVIEWS_NEEDED_TO_DISPLAY } from '../../../constants/reviews';
import useReviewFilters from '../../../utils/hooks/useReviewFilters';
import { useAppContext } from '../../../context/AppContext';
// @ts-expect-error untyped dependency
import { getIsUngatedForReviewEditing } from '../../../selectors/auth';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function Reviews({
  allReviewsHref,
  appName,
  appIcon,
  isPreview,
  isReviewLink,
  listingDetailUrl,
  offeringId,
  offeringPortalId,
  slug,
  supportLink,
  trackInteraction
}) {
  const isUngatedForReviewEditing = useSelector(getIsUngatedForReviewEditing);
  const isPublic = useSelector(getIsPublic);
  const [submitted, setSubmitted] = useState(false);
  const {
    reviewFilters,
    setReviewFilters
  } = useReviewFilters(new ReviewFilters({
    reviewTypes: [ReviewTypes.APP],
    entityId: offeringId,
    sort: RELEVANCE
  }));
  const appContext = useAppContext();
  const {
    reviews,
    reviewStats,
    reviewsFetchStatus,
    reviewStatsFetchStatus,
    reviewsTotal,
    reviewsCount
  } = useReviews({
    appContext,
    reviewFilters,
    offeringId,
    reviewType: 'APP'
  });
  const {
    reviews: userReviews
  } = useReviews({
    appContext,
    reviewFilters: new ReviewFilters({
      reviewTypes: [ReviewTypes.APP],
      entityId: offeringId,
      reviewerPortalId: PortalIdParser.get()
    }),
    offeringId,
    reviewType: 'APP'
  });
  const userReview = userReviews[0];
  const {
    unreviewableReason,
    requestState: reviewabilityRequestStatus
  } = useReviewability({
    editPending: !!userReview && submitted,
    entityId: offeringId,
    entityPortalId: offeringPortalId,
    isPublicApp: isPublic,
    reviewPending: !userReview && submitted,
    reviewType: 'APP'
  });
  const [wizardOpen, setWizardOpen] = useState(isReviewLink);
  const [searchTerm, setSearchTerm] = useState('');
  const renderedWriteReviewButton = useMemo(() => {
    return /*#__PURE__*/_jsx(UIFlex, {
      justify: "end",
      children: /*#__PURE__*/_jsx(WriteReviewButton, {
        className: "m-bottom-4",
        disabled: isPreview,
        hasEditableReview: isUngatedForReviewEditing && !!userReview,
        onClick: () => setWizardOpen(true),
        unreviewableReason: unreviewableReason
      })
    });
  }, [isUngatedForReviewEditing, isPreview, unreviewableReason, userReview]);
  const onUpdateFilters = useCallback(({
    textKey,
    value
  }) => {
    if (textKey === SEARCH_KEY) {
      setSearchTerm(String(value));
    }
    setReviewFilters({
      textKey,
      value
    }, trackInteraction);
  }, [setReviewFilters, trackInteraction]);
  const prevOfferingId = usePrevious(offeringId);
  useEffect(() => {
    // Update the reviewFilters when the offeringId changes
    if (offeringId !== prevOfferingId) {
      setReviewFilters({
        textKey: 'entityId',
        value: offeringId
      });
    }
  }, [offeringId, prevOfferingId, setReviewFilters]);
  const renderedReviewsList = useMemo(() => /*#__PURE__*/_jsxs("div", {
    "data-test-id": "reviewsList",
    style: {
      maxWidth: REVIEWS_LIST_WIDTH
    },
    children: [/*#__PURE__*/_jsx(ReviewsHeader, {
      onUpdateFilters: onUpdateFilters,
      renderedButtonRow: renderedWriteReviewButton,
      reviewsCount: reviewsTotal,
      reviewFilters: reviewFilters
    }), /*#__PURE__*/_jsx(ReviewsFilterSortDropdowns, {
      onUpdateFilters: onUpdateFilters,
      reviewFilters: reviewFilters,
      reviewStats: reviewStats
    }), /*#__PURE__*/_jsx(ReviewsPaginatedList, {
      onUpdateFilters: onUpdateFilters,
      ReviewRenderer: DetailsReviewRenderer,
      reviews: reviews,
      reviewsFetchStatus: reviewsFetchStatus,
      reviewFilters: reviewFilters,
      reviewsTotal: reviewsTotal,
      showPagination: reviewsTotal >= REVIEWS_PER_PAGE,
      searchTerm: searchTerm
    })]
  }), [onUpdateFilters, reviews, reviewStats, reviewsFetchStatus, reviewsTotal, renderedWriteReviewButton, reviewFilters, searchTerm]);
  const renderedReviewsBody = useMemo(() => {
    if (reviewsCount >= MINIMUM_REVIEWS_NEEDED_TO_DISPLAY) {
      return /*#__PURE__*/_jsxs(UIGrid, {
        className: "m-top-8",
        children: [/*#__PURE__*/_jsxs(UIGridItem, {
          size: {
            xs: 12,
            md: 5,
            lg: 4
          },
          children: [/*#__PURE__*/_jsx(LoadingWrapper, {
            requestStatus: reviewStatsFetchStatus,
            children: /*#__PURE__*/_jsx(ReviewsListStats, {
              allReviewsHref: allReviewsHref,
              onUpdateFilters: onUpdateFilters,
              reviewStats: reviewStats,
              reviewType: ReviewTypes.APP
            })
          }), /*#__PURE__*/_jsx(MediaQuery, {
            minScreenSize: "md",
            alternate: /*#__PURE__*/_jsx("div", {
              style: {
                height: 24
              }
            }),
            children: /*#__PURE__*/_jsxs(UIFlex, {
              direction: "column",
              className: "p-top-5",
              children: [/*#__PURE__*/_jsx(H3, {
                "aria-level": 3,
                className: "m-bottom-1 is--heading-5",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "appMarketplace.detail.reviews.write.ctaTitle"
                })
              }), /*#__PURE__*/_jsx(FormattedMessage, {
                message: "appMarketplace.detail.reviews.write.ctaDescription"
              }), /*#__PURE__*/_jsx("div", {
                className: "m-top-4",
                children: /*#__PURE__*/_jsx(WriteReviewButton, {
                  disabled: isPreview,
                  onClick: () => setWizardOpen(true),
                  hasEditableReview: isUngatedForReviewEditing && !!userReview,
                  tooltipPlacement: "right bottom",
                  unreviewableReason: unreviewableReason
                })
              })]
            })
          })]
        }), /*#__PURE__*/_jsx(UIGridItem, {
          size: {
            xs: 12,
            md: 7,
            lg: 8
          },
          children: renderedReviewsList
        })]
      });
    } else if (reviewsCount && reviewsCount < MINIMUM_REVIEWS_NEEDED_TO_DISPLAY) {
      return renderedReviewsList;
    } else if (!reviewsCount) {
      return /*#__PURE__*/_jsx(ReviewsListEmptyState, {
        isPreview: isPreview,
        onWriteReviewClick: () => setWizardOpen(true),
        unreviewableReason: unreviewableReason
      });
    }
    return null;
  }, [isUngatedForReviewEditing, reviewsCount, reviewStatsFetchStatus, allReviewsHref, onUpdateFilters, reviewStats, isPreview, unreviewableReason, userReview, renderedReviewsList]);
  const renderedWizard = useMemo(() => /*#__PURE__*/_jsx(AppReviewWizard, {
    appName: appName,
    appIconUrl: appIcon,
    editedReview: userReview,
    offeringId: offeringId,
    onClose: () => setWizardOpen(false),
    onSuccess: () => {
      setWizardOpen(false);
      replace(listingDetailUrl);
      setSubmitted(true);
    },
    onTrackInteraction: trackInteraction,
    slug: slug,
    supportLink: supportLink || undefined,
    unreviewableReason: unreviewableReason
  }), [appIcon, appName, offeringId, listingDetailUrl, trackInteraction, slug, supportLink, unreviewableReason, userReview]);
  return /*#__PURE__*/_jsx(LoadingWrapper, {
    requestStatus: reviewabilityRequestStatus,
    children: /*#__PURE__*/_jsxs("div", {
      style: {
        position: 'relative'
      },
      children: [/*#__PURE__*/_jsx(H2, {
        className: "m-bottom-8",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "appMarketplace.detail.reviews.headingText",
          options: {
            name: appName
          }
        })
      }), renderedReviewsBody, wizardOpen && renderedWizard]
    })
  });
}